import { Component, ElementRef, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApplicationType, ProjectFormData } from 'app/models/application';
import { TOPIC_GROUPS, TopicGroup } from 'app/models/epe-topics';
import { ApplicationsService } from 'app/services/applications.service';
import { SessionStorage } from 'app/storage/session.storage';
import { switchMap } from 'rxjs/operators';
import { FieldError, focusAndScrollToField } from 'app/shared/forms/form-validation.util';

interface SelectedTopic { value: string; label: string; groupLabel: string; }

@Component({
  selector: 'app-pss-application-form',
  templateUrl: './pss-application-form.component.html',
  styleUrls: [
    '../project-application-form/project-application-form.component.scss',
    '../epe-application-form/epe-application-form.component.scss',
  ]
})
export class PssApplicationFormComponent implements OnInit {
  form!: FormGroup;
  submitting    = false;
  formSubmitted = false;
  errorMessage  = '';
  fieldErrors: FieldError[] = [];
  readonly serviceType: ApplicationType = 'service2';

  draftId    : string | null = null;
  savingDraft = false;
  draftSaved  = false;

  selectedTopics: SelectedTopic[] = [];
  expandedGroups    = new Set<string>();
  expandedSubGroups = new Set<string>();

  onePagerUploading = false;
  onePagerUploadError = '';
  onePagerFileId = '';
  onePagerFilename = '';

  readonly MAX_TOPICS = 3;
  readonly TOPIC_GROUPS: TopicGroup[] = TOPIC_GROUPS;

  readonly EU_FUNDING_PROGRAMMES: string[] = [
    'Horizon Europe',
    'Horizon 2020',
    'LIFE',
    'Digital Europe',
    'Other',
  ];

  readonly SUPPORT_START_OPTIONS: string[] = [
    'As soon as possible',
    'Within 1 month',
    'Within 2 months',
    'Within 3 months',
  ];

  readonly TRL_OPTIONS: string[] = Array.from({ length: 11 }, (_, i) => String(i));

  readonly COUNTRIES: string[] = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda',
    'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
    'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium',
    'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana',
    'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi',
    'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada', 'Central African Republic',
    'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo (Democratic Republic)',
    'Congo (Republic)', 'Costa Rica', "Côte d'Ivoire", 'Croatia', 'Cuba', 'Cyprus',
    'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
    'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea',
    'Estonia', 'Eswatini', 'Ethiopia',
    'Fiji', 'Finland', 'France',
    'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada',
    'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
    'Haiti', 'Honduras', 'Hungary',
    'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
    'Jamaica', 'Japan', 'Jordan',
    'Kazakhstan', 'Kenya', 'Kiribati', 'Kosovo', 'Kuwait', 'Kyrgyzstan',
    'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya',
    'Liechtenstein', 'Lithuania', 'Luxembourg',
    'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta',
    'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia',
    'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar',
    'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua',
    'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway',
    'Oman',
    'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay',
    'Peru', 'Philippines', 'Poland', 'Portugal',
    'Qatar',
    'Romania', 'Russia', 'Rwanda',
    'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
    'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal',
    'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia',
    'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan',
    'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
    'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo',
    'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
    'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States',
    'Uruguay', 'Uzbekistan',
    'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
    'Yemen',
    'Zambia', 'Zimbabwe',
  ];

  readonly SDO_OPTIONS: { group: string; options: string[] }[] = [
    { group: 'European', options: [
      'ASD-STAN',
      'CEN – European Committee for Standardisation',
      'CENELEC – The European Electronic Committee for Standardisation',
      'ETSI',
      'SBS - Small Business Standards',
    ]},
    { group: 'National Standards Bodies', options: [
      'Austria: ASI – Austrian Standards Institute',
      'Austria: OVE - Österreichischer Verband für Elektrotechnik',
      'Belgium: NBN – Bureau de Normalisation',
      'Bulgaria: BDS – Bulgarian Institute for Standardisation',
      'Croatia: HZN – Croation Standards Institute',
      'Cyprus: CYS – Cyprus Organisation for Standardisation',
      'Czech Republic: UNMZ – Czech Office for Standards, Metrology and Testing',
      'Denmark: DS – Dansk Standard',
      'Estonia: EVS – Estonian Centre for Standardisation',
      'Finland: SFS – Finnish Standards Association',
      'France: AFNOR – Association Francaise de normalization',
      'Germany: DIN – Deutsches Institut für Normung e.V.',
      'Germany: DKE - Deutsche Kommission Elektrotechnik Elektronik und Informationstechnik in DIN und VDE',
      'Greece: ELOT – Hellenic Organisation for Standardisation',
      'Hungary: MSZT - Magyar Szabványügyi Testület',
      'Iceland: IST – Icelandic Standards',
      'Ireland: NSAI – National Standards Authority of Ireland',
      'Israel: SII – Standards Institution of Israel',
      'Italy: CEI – Comitato Elettrotecnico Italiano',
      'Italy: UNI – Ente Nazionale Italiano di Unificazione',
      'Latvia: LVS – Standardisation, Accreditation and Metrology Centre, Latvian Standard',
      'Lithuania: LST – Lithuanian Standards Board',
      "Luxembourg: ILNAS – Institut luxembourgeois de la normalisation, de l'accréditation, de la sécurité et qualité des produits et services",
      'Malta: MCCAA – Malta Competition and Consumer Affairs Authority',
      'Moldova, Republic of: ISM – Institute for Standardisation of Moldova',
      'Montenegro: ISME – Institute for Standardisation of Montenegro',
      'Netherlands: NEN – Nederlands Normalisatie-institut',
      'North Macedonia: ISRM – Standardisation Institute of the Republic of Macedonia',
      'Norway: SON – Standards Norway',
      'Norway: NEK - Norsk Elektroteknisk Komite (The Norwegian Electrotechnical Committee)',
      'Poland: PKN – Polski Komitet Normalizacyjny (Polish Committee for Standardization)',
      'Portugal: IPQ - Instituto Português da Qualidade',
      'Romania: ASRO - Asociaţia de Standardizare din România (Romanian Standards Association)',
      'Slovakia: SOSMT – Slovak Office of Standards, Metrology and Testing',
      'Slovenia: SIST – Slovenian Institute for Standardisation',
      'Spain: UNE – Asociación Española de Normalización',
      'Sweden: SEK - Svensk Elstandard',
      'Sweden: SIS – Swedish Institute for Standards',
      'Switzerland: SNV – Swiss Association for Standardisation',
      'Switzerland: CES - Electrosuisse',
      'Turkey: TSE – Turkish Standards Institution',
      'Ukraine: DTSU - State Enterprise "Ukrainian Scientific, Research and Training Center on Standardization, Certification and Quality Problems"',
      'United Kingdom: BSI – British Standards Institution',
    ]},
    { group: 'International', options: [
      'DMTF',
      'IEC – International Electrotechnical Commission',
      'IEEE - Institute of Electrical and Electronics Engineers',
      'IETF - Internet Engineering Task Force',
      'ISO - International Organization for Standardization',
      'ITU - International Telecommunication Union',
      'OGF – Open Grid Forum',
      'OneM2M',
      'UN/CEFACT – Trade Facilitation and E-business',
      'W3C - World Wide Web Consortium',
      'Other – Please specify',
    ]},
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private applicationsService: ApplicationsService,
    private auth: SessionStorage,
    private router: Router,
    private hostRef: ElementRef,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      // Q1 — funding scheme
      funding_type:               ['eu', Validators.required],
      funding_programme:          [''],
      other_funding_programme:    [''],
      national_programme_name:    [''],
      national_programme_country: [''],
      project_call_topic_id:      ['', Validators.required],
      coordinator_country:        ['', Validators.required],
      // Q2 — support requested
      why_the_project_applying:   ['', Validators.required],
      support_expectations:       ['', Validators.required],
      project_short_description:  ['', Validators.required],
      objectives_standardisation: ['', Validators.required],
      trl_start:                  ['', Validators.required],
      trl_target:                 ['', Validators.required],
      applicable_regulations:     [''],
      call_deadline:              ['', Validators.required],
      // Q3 — standards knowledge
      know_standards:             ['no', Validators.required],
      standards_details:          [''],
      nsb_contact:                ['no', Validators.required],
      nsb_contact_name:           [''],
      known_standards_yn:         ['no', Validators.required],
      known_standards_titles:     [''],
      standard_reference:         this.fb.array([]),
      // Q4 — availability & language
      support_start:              ['', Validators.required],
      scheduling_constraints:     [''],
      preferred_languages:        ['', Validators.required],
      time_zone:                  ['', Validators.required],
      // Q5 — consent
      data_consent:               [false, Validators.requiredTrue],
      accuracy_declaration:       [false, Validators.requiredTrue],
    });

    const editId = this.route.snapshot.params['id'];
    if (editId) {
      this.loadExistingApplication(editId);
    }
  }

  private loadExistingApplication(id: string): void {
    this.applicationsService.getById(id).subscribe({
      next: app => {
        if (app.status !== 'draft') {
          this.router.navigate(['/my-applications']);
          return;
        }
        this.draftId = id;
        this.loadDraft(app.form_data as any || {});
      },
      error: () => this.router.navigate(['/my-applications'])
    });
  }

  private resolveTopicMeta(value: string): { label: string; groupLabel: string } | null {
    for (const group of TOPIC_GROUPS) {
      for (const sub of group.subGroups) {
        if (sub.value === value) return { label: sub.label, groupLabel: group.label };
        const child = sub.children.find(c => c.value === value);
        if (child) return { label: child.label, groupLabel: group.label };
      }
    }
    return null;
  }

  private loadDraft(data: any): void {
    if (Array.isArray(data.standard_reference) && data.standard_reference.length > 0) {
      while (this.standardReference.length > 0) this.standardReference.removeAt(0);
      for (const ref of data.standard_reference as any[]) {
        this.standardReference.push(this.fb.group({
          sdo:           [ref.sdo || ''],
          tc_number:     [ref.tc_number || ''],
          tc_title:      [ref.tc_title || ''],
          other_options: [ref.other_options || ''],
        }));
      }
    }
    this.form.patchValue({
      funding_type:               data.funding_type ?? 'eu',
      funding_programme:          data.funding_programme ?? '',
      other_funding_programme:    data.other_funding_programme ?? '',
      national_programme_name:    data.national_programme_name ?? '',
      national_programme_country: data.national_programme_country ?? '',
      project_call_topic_id:      data.project_call_topic_id ?? '',
      coordinator_country:        data.coordinator_country ?? '',
      why_the_project_applying:   data.why_the_project_applying ?? '',
      support_expectations:       data.support_expectations ?? '',
      project_short_description:  data.project_short_description ?? '',
      objectives_standardisation: data.objectives_standardisation ?? '',
      trl_start:                  data.trl_start ?? '',
      trl_target:                 data.trl_target ?? '',
      applicable_regulations:     data.applicable_regulations ?? '',
      call_deadline:              data.call_deadline ?? '',
      know_standards:             data.know_standards ?? 'no',
      standards_details:          data.standards_details ?? '',
      nsb_contact:                data.nsb_contact ?? 'no',
      nsb_contact_name:           data.nsb_contact_name ?? '',
      known_standards_yn:         data.known_standards_yn ?? 'no',
      known_standards_titles:     data.known_standards_titles ?? '',
      support_start:              data.support_start ?? '',
      scheduling_constraints:     data.scheduling_constraints ?? '',
      preferred_languages:        data.preferred_languages ?? '',
      time_zone:                  data.time_zone ?? '',
      data_consent:               data.data_consent ?? false,
      accuracy_declaration:       data.accuracy_declaration ?? false,
    });
    if (data.one_pager_file_id) {
      this.onePagerFileId = data.one_pager_file_id;
      this.onePagerFilename = data.one_pager_filename || '';
    }
    for (const topicValue of (data.topics || []) as string[]) {
      const meta = this.resolveTopicMeta(topicValue);
      if (meta && this.selectedTopics.length < this.MAX_TOPICS) {
        this.selectedTopics.push({ value: topicValue, label: meta.label, groupLabel: meta.groupLabel });
      }
    }
  }

  private buildFormData(): ProjectFormData {
    const v = this.form.value;
    return {
      funding_type:               v.funding_type,
      funding_programme:          v.funding_type === 'eu' ? v.funding_programme : '',
      other_funding_programme:    v.other_funding_programme,
      national_programme_name:    v.funding_type === 'national' ? v.national_programme_name : '',
      national_programme_country: v.funding_type === 'national' ? v.national_programme_country : '',
      project_call_topic_id:      v.project_call_topic_id,
      coordinator_country:        v.coordinator_country,
      topics:                     this.selectedTopics.map(t => t.value),
      why_the_project_applying:   v.why_the_project_applying,
      support_expectations:       v.support_expectations,
      project_short_description:  v.project_short_description,
      objectives_standardisation: v.objectives_standardisation,
      trl_start:                  v.trl_start,
      trl_target:                 v.trl_target,
      applicable_regulations:     v.applicable_regulations,
      call_deadline:              v.call_deadline,
      one_pager_file_id:          this.onePagerFileId,
      one_pager_filename:         this.onePagerFilename,
      know_standards:             v.know_standards,
      standards_details:          v.know_standards === 'yes' ? v.standards_details : '',
      nsb_contact:                v.nsb_contact,
      nsb_contact_name:           v.nsb_contact === 'yes' ? v.nsb_contact_name : '',
      known_standards_yn:         v.known_standards_yn,
      known_standards_titles:     v.known_standards_yn === 'yes' ? v.known_standards_titles : '',
      standard_reference:         v.standard_reference,
      support_start:              v.support_start,
      scheduling_constraints:     v.scheduling_constraints,
      preferred_languages:        v.preferred_languages,
      time_zone:                  v.time_zone,
      data_consent:               v.data_consent,
      accuracy_declaration:       v.accuracy_declaration,
    };
  }

  saveDraft(): void {
    this.savingDraft = true;
    const formData = this.buildFormData();
    const obs$ = (this.draftId
      ? this.applicationsService.updateForm(this.draftId, formData)
      : this.applicationsService.createDraft(this.auth.userId!, this.serviceType, formData)) as any;
    obs$.subscribe({
      next: (res: any) => {
        if (!this.draftId && res?._id) {
          const raw: any = res._id;
          this.draftId = raw?.['$oid'] ?? String(raw);
        }
        this.draftSaved  = true;
        this.savingDraft = false;
        setTimeout(() => { this.draftSaved = false; }, 3000);
      },
      error: () => { this.savingDraft = false; }
    });
  }

  // ── Getters ───────────────────────────────────────────────────────────────────

  get isEuFunded(): boolean { return this.form.get('funding_type')?.value === 'eu'; }
  get isOtherFundingProgramme(): boolean { return this.form.get('funding_programme')?.value === 'Other'; }
  get knowsStandards(): boolean { return this.form.get('know_standards')?.value === 'yes'; }
  get hasNsbContact(): boolean { return this.form.get('nsb_contact')?.value === 'yes'; }
  get knowsRelevantStandards(): boolean { return this.form.get('known_standards_yn')?.value === 'yes'; }

  // ── Validation ────────────────────────────────────────────────────────────────

  isFormValid(): boolean {
    return this.getFieldErrors().length === 0;
  }

  getFieldErrors(): FieldError[] {
    const errors: FieldError[] = [];

    if (this.form.get('funding_type')?.invalid) {
      errors.push({ key: 'funding_type', label: 'Funding type' });
    }
    if (this.isEuFunded && !this.form.get('funding_programme')?.value) {
      errors.push({ key: 'funding_programme', label: 'EU programme' });
    }
    if (!this.isEuFunded) {
      if (!this.form.get('national_programme_name')?.value) {
        errors.push({ key: 'national_programme_name', label: 'National/regional funding programme' });
      }
      if (!this.form.get('national_programme_country')?.value) {
        errors.push({ key: 'national_programme_country', label: 'Funding programme country' });
      }
    }

    const simpleFields: [string, string][] = [
      ['project_call_topic_id', 'Call code'],
      ['coordinator_country', "Coordinating organisation's country"],
      ['why_the_project_applying', 'Why you are applying for Proposal Support Service'],
      ['support_expectations', 'Expectations from HSbooster.eu II proposal support'],
      ['project_short_description', "Future project's objectives, context and expected results"],
    ];
    for (const [key, label] of simpleFields) {
      if (this.form.get(key)?.invalid) errors.push({ key, label });
    }

    if (!this.onePagerFileId) {
      errors.push({ key: 'one_pager', label: 'Project 1-pager upload' });
    }

    const moreFields: [string, string][] = [
      ['objectives_standardisation', 'Standardisation activities mentioned in the call text'],
    ];
    for (const [key, label] of moreFields) {
      if (this.form.get(key)?.invalid) errors.push({ key, label });
    }

    if (this.selectedTopics.length === 0) {
      errors.push({ key: 'topics', label: "Future project's topics" });
    }

    const finalFields: [string, string][] = [
      ['trl_start', 'TRL starting point'],
      ['trl_target', 'Expected TRL after the future project'],
      ['call_deadline', 'Project call deadline'],
      ['support_start', 'When support should start'],
      ['preferred_languages', 'Preferred language(s) for support'],
      ['time_zone', 'Time zone'],
      ['data_consent', 'Data processing consent'],
      ['accuracy_declaration', 'Accuracy declaration'],
    ];
    for (const [key, label] of finalFields) {
      if (this.form.get(key)?.invalid) errors.push({ key, label });
    }

    // Safety net: surface any invalid control this method didn't explicitly label.
    if (this.form.invalid && errors.length === 0) {
      for (const key of Object.keys(this.form.controls)) {
        if (this.form.get(key)?.invalid) errors.push({ key, label: key });
      }
    }

    return errors;
  }

  jumpToField(key: string): void {
    focusAndScrollToField(this.hostRef.nativeElement, key);
  }

  showEuFundingError(): boolean {
    return this.formSubmitted && this.isEuFunded && !this.form.get('funding_programme')?.value;
  }

  showNationalNameError(): boolean {
    return this.formSubmitted && !this.isEuFunded && !this.form.get('national_programme_name')?.value;
  }

  showNationalCountryError(): boolean {
    return this.formSubmitted && !this.isEuFunded && !this.form.get('national_programme_country')?.value;
  }

  // ── Topic picker ──────────────────────────────────────────────────────────────

  toggleGroup(label: string): void {
    this.expandedGroups.has(label) ? this.expandedGroups.delete(label) : this.expandedGroups.add(label);
  }
  isGroupExpanded(label: string): boolean { return this.expandedGroups.has(label); }

  toggleSubGroup(value: string): void {
    this.expandedSubGroups.has(value) ? this.expandedSubGroups.delete(value) : this.expandedSubGroups.add(value);
  }
  isSubGroupExpanded(value: string): boolean { return this.expandedSubGroups.has(value); }

  selectTopic(value: string, label: string, groupLabel: string): void {
    if (this.isTopicSelected(value)) {
      this.selectedTopics = this.selectedTopics.filter(t => t.value !== value);
    } else if (this.selectedTopics.length < this.MAX_TOPICS) {
      this.selectedTopics.push({ value, label, groupLabel });
    }
  }
  isTopicSelected(value: string): boolean { return this.selectedTopics.some(t => t.value === value); }
  removeTopic(value: string): void { this.selectedTopics = this.selectedTopics.filter(t => t.value !== value); }
  canSelectMore(): boolean { return this.selectedTopics.length < this.MAX_TOPICS; }

  // ── Standard reference FormArray ──────────────────────────────────────────────

  get standardReference(): FormArray {
    return this.form.get('standard_reference') as FormArray;
  }

  addStandardReference(): void {
    this.standardReference.push(this.fb.group({
      sdo:           [''],
      tc_number:     [''],
      tc_title:      [''],
      other_options: [''],
    }));
  }

  removeStandardReference(index: number): void {
    this.standardReference.removeAt(index);
  }

  isOtherSdo(refGroup: AbstractControl): boolean {
    return refGroup.get('sdo')?.value === 'Other – Please specify';
  }

  // ── One-pager upload ──────────────────────────────────────────────────────────

  onOnePagerFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      this.onePagerUploadError = 'Only PDF files are accepted.';
      return;
    }
    this.onePagerUploading   = true;
    this.onePagerUploadError = '';
    this.onePagerFilename    = '';
    this.onePagerFileId      = '';
    this.applicationsService.uploadCv(file).subscribe({
      next: (res) => {
        this.onePagerFileId    = res.cv_file_id;
        this.onePagerFilename  = res.filename;
        this.onePagerUploading = false;
      },
      error: (err) => {
        this.onePagerUploadError = err?.error?.error || 'Upload failed. Please try again.';
        this.onePagerUploading   = false;
      }
    });
  }

  // ── Submit ────────────────────────────────────────────────────────────────────

  onSubmit(): void {
    this.formSubmitted = true;
    this.fieldErrors = this.getFieldErrors();
    if (this.fieldErrors.length) {
      focusAndScrollToField(this.hostRef.nativeElement, this.fieldErrors[0].key);
      return;
    }
    this.submitting = true;
    const formData = this.buildFormData();
    if (this.draftId) {
      this.applicationsService.updateForm(this.draftId, formData).pipe(
        switchMap(() => this.applicationsService.updateStatus(this.draftId!, 'pending', this.auth.userId!))
      ).subscribe({
        next: () => this.router.navigate(['/my-applications']),
        error: (err: any) => {
          this.errorMessage = err?.error?.message || 'An error occurred whilst submitting. Please try again.';
          this.submitting = false;
        }
      });
    } else {
      this.applicationsService.create(this.auth.userId!, this.serviceType, formData).subscribe({
        next: () => this.router.navigate(['/my-applications']),
        error: (err: any) => {
          this.errorMessage = err?.error?.message || 'An error occurred whilst submitting. Please try again.';
          this.submitting = false;
        }
      });
    }
  }
}
